<template>
  <div class="search-map-example">
    <Navbar 
      @showHome="goBackToApp"
      @showSignin="goBackToApp"
      @showSignup="goBackToApp"
      @showFeedback="goBackToApp"
    />
    
  <div class="view-selector-container">
    <h2>Hunt Map</h2>
    
    <!-- Utah Resident Checkbox -->
    <div class="resident-checkbox-container">
      <v-checkbox v-model="isUtahResident" label="I am a Utah Resident" />
    </div>
    
    <div class="view-selector">
        <button 
          class="btn" 
          :class="{ 'btn-primary': activeView === 'search', 'btn-outline-primary': activeView !== 'search' }"
          @click="switchView('search')"
        >
          Search Map
        </button>
        <button 
          class="btn" 
          :class="{ 'btn-primary': activeView === 'saved', 'btn-outline-primary': activeView !== 'saved' }"
          @click="switchView('saved')"
        >
          Your Saved Hunts
        </button>
      </div>
      
      <!-- Loading state indicator -->
      <div v-if="isLoading" class="loading-container">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-2">Loading your saved hunts...</p>
      </div>
      
      <!-- Conditionally render the appropriate component -->
      <SearchMapView v-if="activeView === 'search'" v-show="!isLoading" :isUtahResident="isUtahResident" />
      <SavedHuntsMapView 
        v-if="activeView === 'saved'"
        v-show="!isLoading" 
        :isUtahResident="isUtahResident"
        @loading-state-change="handleLoadingStateChange" 
      />
      <div v-if="!activeView" v-show="!isLoading" class="no-map-selected">
        <p>Select a map view above to get started</p>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import SearchMapView from './SearchMapView.vue'
import SavedHuntsMapView from './SavedHuntsMapView.vue'
import Navbar from './Navbar.vue'
import { mapGetters, mapActions } from 'vuex'

export default defineComponent({
  name: 'SearchMapExample',
  components: {
    SearchMapView,
    SavedHuntsMapView,
    Navbar
  },
  data() {
    return {
      activeView: null, // No default view selected
      isUtahResident: true, // Default to true as required
      isLoading: false // Loading state for view transitions
    }
  },
  computed: {
    ...mapGetters(['isAuthenticated'])
  },
  created() {
    // Check authentication when component is created
    console.log('Checking authentication status...')
    if (!this.isAuthenticated) {
      console.log('User is not authenticated. Attempting login...')
      this.attemptLogin();
    }
  },
  methods: {
    ...mapActions(['setUser', 'login']),
    
    goBackToApp() {
      this.$router.push('/');
    },
    
    // Method to handle session expiration/invalid login
    showSignIn() {
      // Redirect to home page with a query parameter to show sign-in
      this.$router.push('/?signin=true');
      // Use a timeout to ensure the navigation happens before showing the alert
      setTimeout(() => {
        alert('Your login session is invalid or has expired. Please sign in again.');
      }, 100);
    },
    
    // Method to handle view switching with loading state
    switchView(view) {
      // Change views immediately but show loading state for saved hunts
      this.activeView = view;
      if (view === 'saved') {
        this.isLoading = true;
      } else {
        this.isLoading = false;
      }
      
      // If switching to saved hunts, set a reasonable timeout as fallback
      // in case the loading-state-change event never gets triggered
      if (view === 'saved') {
        setTimeout(() => {
          if (this.isLoading) {
            this.isLoading = false;
          }
        }, 5000); // 5 second failsafe
      }
    },
    
    // Handle loading state changes from the SavedHuntsMapView component
    handleLoadingStateChange(isLoading) {
      // Only respond to the "loading finished" event
      if (!isLoading) {
        this.isLoading = false;
      }
    },
    
    // Method to attempt login using whoami
    async attemptLogin() {
      try {
        const response = await fetch(this.$API_BASE_URL + '/api/customers/whoami', {
          method: 'GET',
          credentials: "include", // Ensures cookies are sent with the request
          headers: {
            'Content-Type': 'application/json'
          }
        });
        console.log('Login attempt response:', response);
        if (response.ok) {
          const data = await response.json();
          this.setUser(data);
          
          // Set expiration to 24 hours from now
          const expiration = new Date();
          expiration.setHours(expiration.getHours() + 24);
          this.login(expiration.toISOString());
        } else {
          console.error('Failed to fetch user information');
          // this.showSignIn();
        }
      } catch (error) {
        console.error('Error during login attempt:', error);
        // this.showSignIn();
      }
    }
  }
})
</script>

<style scoped>
.search-map-example {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.view-selector-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 1rem;
  width: 100%;
}

.resident-checkbox-container {
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
  max-width: 300px;
  margin-left: auto;
  margin-right: auto;
}

.view-selector-container h2 {
  text-align: center;
  margin-bottom: 1rem;
}

.view-selector {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 2rem;
}

.no-map-selected {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  border: 1px dashed #ccc;
  border-radius: 8px;
  background-color: #f9f9f9;
}

.no-map-selected p {
  font-size: 1.2rem;
  color: #666;
}

.loading-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 300px;
  border: 1px dashed #ccc;
  border-radius: 8px;
  background-color: #f9f9f9;
}

.loading-container .spinner-border {
  width: 3rem;
  height: 3rem;
  color: #0d6efd;
}

.loading-container p {
  font-size: 1.2rem;
  color: #666;
  margin-top: 1rem;
}

@media (max-width: 768px) {
  .view-selector {
    flex-direction: column;
    align-items: center;
  }
  
  .view-selector button {
    width: 100%;
    max-width: 300px;
  }
}
</style>
